import React, { ElementType } from "react";
import { Select, SelectProps, SxProps, Theme } from '@mui/material';
import { Colors } from '@sweatpals/react-ui-kit';
import { CoreFormControl, CoreFormControlProps } from "../core-form-control";
import { ArrowDownIcon } from '../../icons';

export type CoreSelectProps<Value> = CoreFormControlProps & SelectProps<Value> & {
	IconComponent?: ElementType;
	paperSx?: SxProps<Theme>;
	onClose?(): void;
};

export function CoreSelect<Value = unknown>(props: CoreSelectProps<Value>) {
	const {
		children,
		focused,
		helperText,
		error,
		controlSx,
		labelSx,
		disabled,
		fullWidth,
		label,
		requiredMark,
		controlId,
		IconComponent,
		onClose,
		paperSx,
		MenuProps: menuProps,
		sx,
		...selectProps
	} = props;

	return (
		<CoreFormControl
			label={label}
			controlId={controlId}
			labelSx={labelSx}
			requiredMark={requiredMark}
			fullWidth={fullWidth}
			focused={focused}
			controlSx={controlSx}
			helperText={helperText}
			error={error}
			disabled={disabled}
		>
			<Select
				{...selectProps}
				error={error}
				sx={[
					{
						'& .MuiSelect-select': {
							borderRadius: '12px',
							minHeight: 'unset',
							backgroundColor: Colors.white_a5,
							color: Colors.white,
							p: '13px 16px',
							fontFamily: 'Montserrat',
							fontSize: '16px',
							fontWeight: 500,
							lineHeight: '20px',

							'&.MuiInputBase-input.MuiOutlinedInput-input': {
								pr: '42px',
							},
						},

						'& .MuiSelect-icon': {
							top: '50%',
							right: '16px',
							transform: 'translateY(-50%)',
							color: Colors.white_a60,
						},

						'& .MuiSelect-iconOpen': {
							transform: 'translateY(-50%) rotate(180deg)',
						},

						'& .MuiOutlinedInput-notchedOutline': {
							borderWidth: 0,
							borderRadius: '12px',
						},

						'&:hover': {
							'& .MuiSelect-icon': {
								color: Colors.white_a80,
							},

							'.MuiOutlinedInput-notchedOutline': {
								borderWidth: '1px',
								borderColor: Colors.white_a5,
							},
						},

						'&.Mui-focused': {
							'& .MuiSelect-icon': {
								color: Colors.white_a80,
							},

							'& .MuiOutlinedInput-notchedOutline': {
								borderWidth: '1px',
								borderColor: Colors.white_a60,
							},
						},

						'&.Mui-error': {
							'& .MuiSelect-icon': {
								color: Colors.white_a80,
							},

							'.MuiOutlinedInput-notchedOutline': {
								borderWidth: '1px',
								borderColor: Colors.error,
							},
						},

						'&.Mui-disabled': {
							'& .MuiSelect-icon': {
								color: Colors.white_a60,
							},

							'& .MuiSelect-select': {
								WebkitTextFillColor: 'unset',
								color: Colors.white_a60,
							},

							'.MuiOutlinedInput-notchedOutline': {
								borderWidth: '1px',
								borderColor: Colors.black_a20,
							}
						},
					},
					...(Array.isArray(sx) ? sx : [sx]),
				]}
				MenuProps={{
					...(menuProps ?? {}),
					TransitionProps: {
						...(menuProps?.TransitionProps ?? {}),
						onExited: onClose
					},
					PaperProps: {
						sx: [
							{
								borderRadius: '8px',
								backgroundColor: Colors.black,

								'& .MuiList-root': {
									p: 0,

									'& .MuiMenuItem-root': {
										p: '12px 16px',
										fontSize: '16px',
										fontWeight: 500,
										lineHeight: '20px',
										fontFamily: 'Montserrat',
										color: Colors.white,
										backgroundColor: '#0D0D0DCC',

										'&:hover, &.Mui-selected': {
											backgroundColor: Colors.white_a10,
										},
									},
								},
							},
							...(Array.isArray(paperSx) ? paperSx : [paperSx])
						]
					},
				}}
				IconComponent={IconComponent || ArrowDownIcon}
			>
				{children}
			</Select>
		</CoreFormControl>
	);
}
